import { ComponentType, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { MenuLinkItem } from '@rossum/components/menu-link-item/MenuLinkItem';
import {
  IconLogout,
  IconReceipt,
  IconUserCircle,
} from '@rossum/ui/icons/tabler';
import {
  Divider,
  IconButton,
  Menu,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { isEmbedded } from '../../constants/config';
import { LocalizationKeys } from '../../i18n';
import { logoutUser } from '../../redux/modules/auth/actions';
import { useBillingEnabled } from '../billing';
import { ChatWidget } from '../chat';
import { organizationBranding } from '../client-config/organization/branding';
import { useIsFeatureEnabled } from '../feature-enablement/featureEnablement';
import { HelpPanel } from '../help-panel';
import { personalInfoPath } from '../personal-settings/routes';
import { hasSubscriptionPlanExistSelector } from '../pricing/selectors';
import { BeamerToggle } from './components/BeamerToggle';
import { OrganizationSwitcher } from './components/OrganizationSwitcher';
import { UserAvatar } from './components/UserAvatar';
import { useCurrentOrganization } from './hooks/useCurrentOrganization';
import { useUser } from './hooks/useUser';

type StaticMenuItem = {
  key: LocalizationKeys;
  Icon: ComponentType;
  dataCy: string;
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
};

export const UserPanel = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const closeUserMenu = () => setAnchorEl(null);

  const { data: user } = useUser();
  const { data: currentOrganization } = useCurrentOrganization();

  const intl = useIntl();
  const dispatch = useDispatch();

  const isChatEnabled = useIsFeatureEnabled('mr-fabry');

  const isPricingActive = useSelector(hasSubscriptionPlanExistSelector);
  const billingPath = `/billing/${isPricingActive ? 'my-plan' : 'overview'}`;

  const billingEnabled = useBillingEnabled().isEnabled;

  const staticMenuItems: Array<StaticMenuItem> = [
    {
      dataCy: 'personal-settings-link',
      Icon: IconUserCircle,
      href: personalInfoPath(),
      key: 'features.personalSettings.title',
    },
    ...(billingEnabled
      ? [
          {
            dataCy: 'user-panel-billing',
            Icon: IconReceipt,
            href: billingPath,
            key: 'containers.billing.title' as const,
          },
        ]
      : []),

    {
      dataCy: 'logout-button',
      Icon: IconLogout,
      key: 'components.dashboard.user.signOut' as const,
      onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        dispatch(logoutUser());
      },
    },
  ];

  const email = user?.email ?? user?.username ?? '';
  const firstName = user?.firstName ?? '';
  const lastName = user?.lastName ?? '';

  const userInitials = `${(firstName || user?.username || email)[0]}${
    lastName[0] || ''
  }`.toUpperCase();

  const isDemoMode = useIsFeatureEnabled('demo-mode');

  const uiSettings = isDemoMode ? {} : (currentOrganization?.uiSettings ?? {});

  const topRightLogo = organizationBranding.customUserAvatarImage(uiSettings);
  const supportMenuItems = organizationBranding.supportMenuItems(uiSettings);

  return (
    <Stack spacing={1} direction="row" alignItems="center">
      {isChatEnabled ? <ChatWidget /> : null}
      <HelpPanel supportMenuItems={supportMenuItems} />
      <BeamerToggle />
      {currentOrganization && user ? (
        isEmbedded() ? (
          topRightLogo ? (
            <UserAvatar avatarUrl={topRightLogo} userInitials="" />
          ) : null
        ) : (
          <Stack>
            <Menu
              open={isOpen}
              anchorEl={anchorEl}
              onClose={closeUserMenu}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              slotProps={{
                list: {
                  sx: {
                    width: 334,
                  },
                },
                paper: {
                  sx: {
                    overflow: 'visible',
                  },
                },
              }}
            >
              <Stack px={2} py={1} direction="row" alignItems="center">
                <UserAvatar
                  avatarUrl={topRightLogo}
                  userInitials={userInitials}
                />
                <Stack sx={{ marginLeft: 1 }}>
                  <Typography variant="subtitle2" color="text.primary" noWrap>
                    {`${firstName} ${lastName}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    data-cy="email"
                  >
                    {email}
                  </Typography>
                </Stack>
              </Stack>
              <Divider />
              <OrganizationSwitcher organization={currentOrganization} />
              <Divider />
              {staticMenuItems.map(({ key, href, Icon, dataCy, onClick }) => (
                <MenuLinkItem
                  key={key}
                  href={href ?? '#'}
                  dataCy={dataCy}
                  Icon={Icon}
                  label={intl.formatMessage({ id: key })}
                  onClick={e => {
                    closeUserMenu();
                    if (onClick) onClick(e);
                  }}
                />
              ))}
            </Menu>
            <IconButton
              size="small"
              sx={{ height: 'unset', width: 'unset' }}
              aria-controls={isOpen ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isOpen ? 'true' : undefined}
              data-cy="userpanel"
              onClick={e => setAnchorEl(e.currentTarget)}
            >
              <UserAvatar
                avatarUrl={topRightLogo}
                userInitials={userInitials}
              />
            </IconButton>
          </Stack>
        )
      ) : null}
    </Stack>
  );
};
